import type { DesignProblem, DesignProblemCode } from '@pz/scene';
import type { DesignerTexts } from '../designer-types.js';
import { PHONE_TEXT_TABLE } from './phone/texts-phone.js';

/**
 * Every word the designer says, in Serbian.
 *
 * The shopper-facing seam (`DesignerTexts`) carries the strings a shop is
 * likely to want in its own voice — the tool names, the quality verdicts, the
 * "Gotovo" button. Everything else the designer needs to say lives in
 * `DESIGNER_EXTRA_TEXTS` below: still one file, still overridable at runtime
 * (the host may pass any of these keys through `options.texts`), but not part
 * of the typed contract until a shop actually asks for it.
 *
 * Placeholders are `{name}` and filled by `fillText`. Never build a sentence
 * by concatenation: Serbian cases make word order part of the translation.
 */

export const DEFAULT_DESIGNER_TEXTS: DesignerTexts = {
  title: 'Dizajniraj',
  done: 'Gotovo',
  cancel: 'Otkaži',
  addText: 'Tekst',
  addImage: 'Slika',
  addArtwork: 'Ukrasi',
  addEmoji: 'Emoji',
  addShape: 'Oblici',
  layers: 'Slojevi',
  empty: 'Dodaj tekst, sliku ili ukras',
  printSize: 'Veličina štampe: {size}',
  safeArea: 'Bezbedna zona',
  outside: 'Deo dizajna je van polja za štampu',
  qualityHigh: 'Odličan kvalitet',
  qualityMid: 'Dobar kvalitet',
  qualityLow: 'Može biti malo mekša na štampi',
  qualityBlocked: 'Slika je u redu i može se koristiti. Veća slika daje još oštriji otisak.',
  textPlaceholder: 'Upiši tekst',
  font: 'Font',
  color: 'Boja',
  outline: 'Okvir',
  shadow: 'Senka',
  bend: 'Krivina',
  size: 'Veličina',
  align: 'Poravnanje',
  spacing: 'Razmak',
  uppercase: 'VELIKA SLOVA',
  style: 'Stil',
  opacity: 'Prozirnost',
  flip: 'Obrni',
  duplicate: 'Kopiraj',
  remove: 'Obriši',
  forward: 'Napred',
  backward: 'Nazad',
  crop: 'Iseci sliku',
  removeBackground: 'Ukloni pozadinu',
  filters: 'Filteri',
  recolor: 'Preboj',
  fill: 'Ispuna',
  stroke: 'Linija',
  undo: 'Vrati',
  redo: 'Ponovi',
  search: 'Traži',
  noResults: 'Nema rezultata',
  loading: 'Učitavam…',
  maxLayers: 'Najviše {n} elemenata',
  uploadHint: 'JPG, PNG, HEIC do 40 MB',
  price: 'Doplata: {price}',
  tooManyChars: 'Najviše {n} znakova',
};

/**
 * The rest of the designer's vocabulary. Same file, same override path, but
 * outside `DesignerTexts` so the seam between the designer and the widget
 * stays the short list a shop reasonably wants to rewrite.
 */
export const DESIGNER_EXTRA_TEXTS = {
  // The phone shell's own vocabulary, `ph`-prefixed so it cannot collide with
  // a desktop key that happens to share a word ("Veličina" is a sub-panel
  // title there and a rail label here). Merged in so a shop overrides both
  // shells through the one `options.texts` object.
  ...PHONE_TEXT_TABLE,

  close: 'Zatvori',
  back: 'Nazad',
  none: 'Bez',
  customColor: 'Druga boja',
  moreColors: 'Sve boje',
  singleColor: 'Jedna boja',
  originalColors: 'Originalne boje',
  tint: 'Preboj u boju',
  upload: 'Izaberi sliku',
  uploadCamera: 'Fotografiši',
  uploading: 'Otpremam…',
  uploadFailed: 'Slika nije otpremljena. Probaj ponovo.',
  uploadTooMany: 'Najviše {n} slika',
  dropHint: 'Prevuci sliku ovde ili je izaberi',
  cropHint: 'Povuci ivice da isečeš sliku ili pomeri oblast isecanja',
  zoom: 'Uvećanje',
  straighten: 'Ispravi',
  brightness: 'Osvetljenje',
  contrast: 'Kontrast',
  filterNone: 'Bez filtera',
  filterGray: 'Crno-belo',
  filterSepia: 'Sepija',
  cutoutWorking: 'Uklanjam pozadinu…',
  cutoutFailed: 'Pozadina nije uklonjena',
  cutoutOn: 'Pozadina uklonjena',
  quality: 'Kvalitet za štampu',
  outlineNone: 'Bez okvira',
  outlineThin: 'Tanak',
  outlineMedium: 'Srednji',
  outlineThick: 'Debeo',
  strokeWidth: 'Debljina linije',
  bendStraight: 'Pravo',
  alignLeft: 'Levo',
  alignCenter: 'Sredina',
  alignRight: 'Desno',
  lines: 'Redova: {n}/{max}',
  chars: 'Znakova: {n}/{max}',
  tooManyLines: 'Najviše {n} reda',
  rotate: 'Okreni',
  scale: 'Razvuci',
  lock: 'Zaključaj',
  unlock: 'Otključaj',
  hide: 'Sakrij dok radim',
  show: 'Prikaži',
  hiddenNote: 'Sakriveni slojevi se ipak štampaju — skrivanje važi samo dok dizajniraš.',
  reorderHint: 'Prevuci da promeniš red',
  nothingHere: 'Ovde još nema ničega',
  layerText: 'Tekst',
  layerImage: 'Slika',
  layerArtwork: 'Ukras',
  layerShape: 'Oblik',
  backdrop: 'Podloga',
  backdropLight: 'Svetla',
  backdropDark: 'Tamna',
  backdropCustom: 'Druga',
  backdropProduct: 'Proizvod',
  allLibraries: 'Sve',
  more: 'Prikaži još',
  fixFirst: 'Popravi ovo pa nastavi',
  emptyTitleShort: 'Prazno polje',
  selectLayer: 'Izaberi element na dizajnu',
  toolNotAllowed: 'Ovaj alat nije dostupan za ovaj proizvod',
  artworkFailed: 'Ukrasi se ne učitavaju',
  artworkRetry: 'Pokušaj ponovo',
  artworkCount: '{n} ikona i ukrasa u katalogu',
  designerLabel: 'Dizajner polja za štampu',
  canvasLabel: 'Polje za štampu',
  panelLabel: 'Alat: {tool}',
  zoneEmptyHint: 'Sve što dodaš ostaje unutar isprekidane linije.',

  // ── Desktop header and tabs ───────────────────────────────────────────────
  saveExit: 'Sačuvaj i izađi',
  savedMark: 'Sačuvano',
  savingMark: 'Čuvam…',
  tabDesign: 'Dizajn',
  tabReview: 'Pregled',
  nextReview: 'Dalje: Pregled',
  nextSurface: 'Dalje: {name}',
  nextOptions: 'Dalje: Opcije',
  tabOptions: 'Opcije',
  optionsTitle: 'Izaberi opcije',
  optionsHint: 'Veličina, boja i količina važe za ceo proizvod. Dizajn ostaje isti.',
  previewOnProduct: 'Prikaz na proizvodu',
  previewBack: 'Nastavi uređivanje',
  surfacePick: 'Strana proizvoda',
  libraryTitle: 'Moji dizajni',
  libraryName: 'Naziv dizajna',
  librarySave: 'Sačuvaj',
  librarySaved: 'Sačuvano u Moje dizajne.',
  libraryEmpty: 'Još nema sačuvanih dizajna.',
  libraryOpen: 'Otvori',
  libraryRemove: 'Ukloni sa spiska',
  libraryNote: 'Dizajn se čuva na našem serveru i otvara se sa linka na bilo kom uređaju; spisak se pamti u ovom pregledaču.',
  printLocation: 'Lokacija štampe',
  printLocationNote: 'Dizajn prelazi na izabranu lokaciju i srazmerno se prilagođava njenoj veličini.',
  sidesTitle: 'Strane',

  // ── Rail ──────────────────────────────────────────────────────────────────
  toolTemplates: 'Šabloni',
  toolImages: 'Slike',
  toolBackground: 'Pozadina',
  toolElements: 'Elementi',
  toolIcons: 'Ikone',
  toolSticker: 'Nalepnica',

  // ── Contextual toolbar ────────────────────────────────────────────────────
  editText: 'Uredi tekst',
  fontNamed: 'Font: {name}',
  textColor: 'Boja teksta',
  bold: 'Podebljano',
  italic: 'Kurziv',
  textAlign: 'Poravnanje teksta',
  makeSmaller: 'Smanji',
  makeLarger: 'Povećaj',
  alignmentTool: 'Poravnanje',
  effectsTool: 'Efekti',
  changeImage: 'Zameni sliku',
  cropTool: 'Iseci',
  cropMore: 'Više opcija',
  cropWindow: 'Oblast isecanja: prevucite da pomerite sliku, točkić ili dva prsta za uvećanje, ručice menjaju okvir. Enter primenjuje, Escape odustaje.',
  fitToZone: 'Uklopi',
  fillZone: 'Popuni',
  fillColor: 'Boja ispune',
  lineColor: 'Boja linije',
  lineWeight: 'Debljina linije (mm)',
  editQr: 'Uredi QR',
  moreTools: 'Još alatki',
  multiSelected: 'Izabrano: {n}',

  // ── Alignment panel ───────────────────────────────────────────────────────
  alignSection: 'Poravnaj',
  alignTop: 'Gore',
  alignMiddle: 'Sredina uspravno',
  alignBottom: 'Dole',
  relativeTo: 'U odnosu na',
  relativeObjects: 'Izabrane elemente',
  relativeDesign: 'Polje za štampu',
  distributeSection: 'Ravnomerno rasporedi',
  distributeH: 'Vodoravno',
  distributeV: 'Uspravno',
  flipSection: 'Obrni',
  scaleSection: 'Veličina',
  stepLess: 'Manje',
  stepMore: 'Više',
  rotateSection: 'Rotacija',
  rotateCcw: 'Rotiraj ulevo',
  rotateCw: 'Rotiraj udesno',
  rotationAngle: 'Ugao (°)',
  groupSection: 'Grupa',
  group: 'Grupiši',
  ungroup: 'Razgrupiši',
  positionSection: 'Položaj i veličina',
  widthMm: 'Širina (mm)',
  heightMm: 'Visina (mm)',
  posX: 'X (mm)',
  posY: 'Y (mm)',

  // ── Effects panel ─────────────────────────────────────────────────────────
  tiling: 'Šare',
  tileNone: 'Bez',
  tileBasic: 'Mreža',
  tileCentered: 'Centrirano',
  whiteRemoval: 'Ukloni belu boju',
  whiteNever: 'Nikad',
  whiteBorder: 'Samo pozadina',
  whiteAll: 'Svu belu',
  whiteRemovalNote: '„Samo pozadina” uklanja belo koje dodiruje ivicu slike; belo unutar motiva (oči, majica, slova) ostaje.',
  tileHalfBrick: 'Pola cigle',
  tileHalfDrop: 'Pola pada',
  tileMirror: 'Ogledalo',
  gapX: 'Razmak vodoravno (mm)',
  gapY: 'Razmak uspravno (mm)',
  advanced: 'Napredno',
  shadowSwitch: 'Senka',
  shadowDirection: 'Smer',
  shadowOffset: 'Udaljenost',
  blurAmount: 'Zamućenje',
  strokeSwitch: 'Okvir slova',
  thickness: 'Debljina',
  lineSpacingLabel: 'Razmak redova',
  letterSpacing: 'Razmak slova',
  textBoxSection: 'Okvir teksta',
  fixedWidthSwitch: 'Fiksna širina okvira',
  fixedWidthOn: 'Tekst se prelama na širinu okvira, a visina prati broj redova.',
  fixedWidthOff: 'Automatska širina — okvir prati dužinu teksta.',
  wrapWidth: 'Širina prelamanja (mm)',
  verticalAnchor: 'Sidro',
  anchorTop: 'Sidro gore',
  anchorMiddle: 'Sidro sredina',
  anchorBottom: 'Sidro dole',
  anchorHint: 'Ivica koja ostaje na mestu kada se promeni broj redova.',
  curvedText: 'Zakrivljen tekst',
  orientationLabel: 'Smer teksta',
  horizontal: 'Vodoravno',
  vertical: 'Uspravno',
  whiteTransparent: 'Bela kao providna',
  keepAspect: 'Zadrži proporcije',
  imageEffects: 'Efekti slike',
  textEffects: 'Efekti teksta',
  shapeEffects: 'Efekti oblika',

  // ── Colour block ──────────────────────────────────────────────────────────
  customColorTitle: 'Prilagođena boja',
  eyedropper: 'Izaberi boju iz dizajna',
  eyedropperHint: 'Kliknite na dizajn da uzmete boju. Esc otkazuje.',
  eyedropperFailed: 'Boju nije moguće uzeti sa ovog mesta',
  hexLabel: 'Heks boja',
  hexInvalid: 'Unesite boju kao #rrggbb',
  palettes: 'Palete',
  showAllColors: 'Prikaži sve',
  showFewerColors: 'Prikaži manje',
  additionalColors: 'Dodatne boje',
  printColors: 'Za štampu',
  webColors: 'Web',
  noColor: 'Bez boje',

  // ── Font picker ───────────────────────────────────────────────────────────
  fontsTitle: 'Fontovi',
  fontSearch: 'Pretraži fontove',
  fontsPopular: 'Popularni',
  fontsAll: 'Svi',
  fontsInDesign: 'Fontovi u ovom dizajnu',
  fontVariants: 'Prikaži stilove',
  catSerif: 'Serif',
  catSans: 'Sans',
  catScript: 'Kaligrafija',
  catHandwriting: 'Rukopis',
  catDisplay: 'Display',
  catDecorative: 'Dekorativni',
  catMono: 'Mono',
  catSymbols: 'Simboli',
  catCyrillic: 'Ćirilica',

  // ── Elementi ──────────────────────────────────────────────────────────────
  dynamicShapes: 'Osnovni oblici',
  addQr: 'Dodaj QR kod',
  qrTitle: 'Upišite adresu',
  qrUrlLabel: 'Internet adresa',
  qrScanHint: 'Skenirajte probni otisak kamerom da proverite da kod radi.',
  qrMinSize: 'Najmanja veličina za ovu adresu: {n} mm.',

  // ── Slike ─────────────────────────────────────────────────────────────────
  fromComputer: 'Sa računara',
  fromPhone: 'Sa telefona',
  phoneQrTitle: 'Skenirajte kod telefonom',
  phoneQrHint: 'Otvorite kameru na telefonu i skenirajte kod. Izabrane slike se pojave ovde.',
  phoneWaiting: 'Čekam slike sa telefona…',
  phoneExpired: 'Veza sa telefonom je istekla. Napravite novi kod.',
  phoneReceived: 'Stiglo slika: {n}',
  phoneFailed: 'Povezivanje sa telefonom nije uspelo',
  sessionUploads: 'Vaše slike',
  replacePicked: 'Zameni ovom slikom',
  replaceMode: 'Izaberite sliku koja menja postojeću.',

  // ── Bottom bar ────────────────────────────────────────────────────────────
  zoomOut: 'Umanji prikaz',
  zoomIn: 'Uvećaj prikaz',
  zoomLevel: 'Nivo uvećanja',
  zoomFit: 'Uklopi',
  settings: 'Podešavanja',
  help: 'Pomoć',
  shareDesign: 'Deli',
  shareTitle: 'Podelite ovaj dizajn',
  shareMake: 'Napravi link za deljenje',
  shareWorking: 'Pravim link…',
  shareCopy: 'Kopiraj link',
  shareCopied: 'Link je kopiran',
  shareNote: 'Ko otvori link može da vidi, izmeni ili kupi ovaj dizajn.',
  shareFailed: 'Deljenje nije uspelo.',

  // ── Settings dialog ───────────────────────────────────────────────────────
  setMiniToolbar: 'Prikaži mini alatke',
  setMiniToolbarNote: 'Alatke iznad izabranog elementa',
  setDarkMode: 'Tamni režim',
  setDarkModeNote: 'Tamna pozadina radnog prostora',
  setLockAspect: 'Zaključaj proporcije',
  setLockAspectNote: 'Elementi zadržavaju odnos stranica',
  setSnap: 'Privlačenje',
  setSnapNote: 'Elementi se privlače uz vodiče i mrežu',
  setGuidelines: 'Prikaži sve vodiče',
  setGuidelinesNote: 'Linije za sečenje, vidljivi deo i bezbednu zonu',
  setRulers: 'Prikaži lenjire',
  setRulersNote: 'Stvarne dimenzije polja za štampu',
  setGrid: 'Prikaži mrežu',
  setGridNote: 'Mreža preko platna',
  setTransparency: 'Prikaži providnost',
  setTransparencyNote: 'Šahovska podloga umesto boje',
  fileSection: 'Datoteka dizajna',
  fileDownload: 'Preuzmi dizajn (.json)',
  fileLoad: 'Učitaj dizajn',
  fileReplace: 'Zameni dizajn',
  fileAppend: 'Dodaj u dizajn',
  fileFitScale: 'Uklopi proporcionalno',
  filePhysical: 'Zadrži milimetre',
  fileCopyLayers: 'Kopiraj izabrane slojeve',
  filePasteLayers: 'Nalepi slojeve',
  fileNote: 'Sačuvajte raspored za kasnije ili prenesite dizajn u kompatibilno polje. Fontovi i slike moraju biti dostupni u ovoj prodavnici.',
  fileTooBig: 'Datoteka je veća od 128 KB',
  fileBroken: 'Datoteka nije ispravan dizajn',
  fileMissingAssets: 'Dodajte slike iz ovog dizajna pre učitavanja. Datoteka čuva njihov identitet, ne kopira privatne slike.',
  viewNote: 'Podešavanja prikaza ne menjaju datoteku za štampu.',

  // ── Help popover ──────────────────────────────────────────────────────────
  helpBleed: 'Linija sečenja',
  helpBleedNote: 'Pozadinske slike proširite do ove linije da ne bi ostale bele ivice',
  helpVisible: 'Vidljiva ivica',
  helpVisibleNote: 'Ovo je vidljivi deo dizajna',
  helpSafe: 'Bezbedna zona',
  helpSafeNote: 'Važan tekst i slike držite unutar ove zone',
  shortcutsButton: 'Prečice na tastaturi',
  shortcutsTitle: 'Prečice na tastaturi',

  // ── Keyboard table ────────────────────────────────────────────────────────
  keySelectAll: 'Izaberi sve',
  keyNudgeSmall: 'Pomeri za 0,5 mm',
  keyNudge: 'Pomeri za 1 mm',
  keyNudgeBig: 'Pomeri za 5 mm',
  keyDelete: 'Obriši',
  keyAddText: 'Dodaj tekst',
  keyCut: 'Iseci',
  keyCopy: 'Kopiraj',
  keyPaste: 'Nalepi',
  keyScaleUp: 'Uvećaj element',
  keyScaleUpFine: 'Uvećaj malo',
  keyScaleDown: 'Umanji element',
  keyScaleDownFine: 'Umanji malo',
  keyZoomIn: 'Uvećaj prikaz',
  keyZoomOut: 'Umanji prikaz',
  keyZoomFit: 'Uklopi prikaz',
  keyUndo: 'Vrati',
  keyRedo: 'Ponovi',
  keyDuplicate: 'Kopiraj element',
  keyGroup: 'Grupiši',
  keyUngroup: 'Razgrupiši',
  keyEscape: 'Poništi izbor',
  keyArrows: 'Strelice',

  // ── Review tab ────────────────────────────────────────────────────────────
  reviewTitle: 'Da proverimo da je sve kako treba',
  reviewLead: 'Pregledajte dizajn pre nego što nastavite.',
  checkNames: 'Proverite imena i datume',
  checkCut: 'Ništa nije odsečeno',
  checkTemplates: 'Svi šablonski elementi su izmenjeni',
  checkSides: 'Proverite sve strane',
  fixProblem: 'Ispravi',
  noProblems: 'Nema grešaka — dizajn je spreman.',
  blockingCount: 'Greške koje zaustavljaju porudžbinu: {n}',

  // ── Context menu ──────────────────────────────────────────────────────────
  menuCopy: 'Kopiraj',
  menuPaste: 'Nalepi',
  menuDelete: 'Obriši',
  menuSendBack: 'Pošalji nazad',
  menuBackward: 'Korak nazad',
  menuForward: 'Korak napred',
  menuFront: 'Dovedi napred',
  menuShowLayers: 'Prikaži slojeve',
  copiedLayers: 'Slojevi su kopirani',
  copyNothing: 'Izaberite slojeve',
  pasteNothing: 'Prvo kopirajte slojeve',

  // ── Nalepnica ─────────────────────────────────────────────────────────────
  stickerNote: 'Beli obod i mostovi povezuju slova, ukrase i fotografije u jedan komad. Fotografija zadržava svoju ivicu, bez dodatnog belog oboda.',
  stickerOn: 'Napravi nalepnicu',
  stickerOff: 'Bez nalepnice',
  stickerBorder: 'Obod teksta i ukrasa (mm)',
  stickerBridge: 'Širina spojeva (mm)',
  stickerCutNote: 'Isprekidana linija pokazuje gde se nalepnica seče. Ne štampa se.',
  stickerPreparing: 'Priprema oblika za sečenje…',
  stickerReady: 'Jedan povezan oblik · linija se ne štampa',
  stickerDuringDrag: 'Oblik se prerađuje…',
  stickerRequired: 'Ovaj proizvod se uvek seče kao nalepnica.',
  stickerOutline: 'Beli obod',
  stickerSnug: 'Tanak, po obliku',
  stickerEven: 'Ravnomeran',
  stickerSnugHint: 'Obod prati slova i spaja ih taman toliko da nalepnica bude jedan komad.',
  stickerEvenHint: 'Isti široki obod svuda oko dizajna.',
  stickerRim: 'Najtanji obod (mm)',
  stickerAdvanced: 'Napredno',

  // ── Quick start (the empty zone) ──────────────────────────────────────────
  startTitle: 'Šta želiš da odštampaš?',
  startPhoto: 'Slika',
  startPhotoHint: 'Tvoja fotografija',
  startText: 'Tekst',
  startTextHint: 'Ime ili poruka',
  startBoth: 'Slika i tekst',
  startBothHint: 'Fotografija sa imenom',
  startTemplates: 'Gotovi šabloni',
  startIcons: 'Ukrasi',
  askTextTitle: 'Upiši ime ili poruku',
  askTextPlaceholder: 'npr. Mila',
  askTextOk: 'Dalje',
  askTextSkip: 'Preskoči',
  pickingPhoto: 'Učitavam sliku…',
  photoPickCancelled: 'Slika nije izabrana.',

  // ── Auto-arrange ──────────────────────────────────────────────────────────
  arrangeTitle: 'Rasporedi',
  arrangeHint: 'Dodirni raspored koji ti se sviđa. „Vrati” poništava.',
  arrangeApplied: 'Raspoređeno',

  // ── Templates and template objects ────────────────────────────────────────
  templateFields: 'Personalizuj tekstove',
  templateSearch: 'Pretraži šablone',
  templateCategory: 'Kategorija',
  templateAll: 'Sve kategorije',
  templateNone: 'Nema kompatibilnih šablona za ovo polje.',
  templateNote: 'Dodirni šablon — tvoja slika i ime prelaze u njega. „Vrati” vraća prethodni dizajn.',
  templateKindAll: 'Sve',
  templateKindPhotoText: 'Slika + tekst',
  templateKindText: 'Tekst',
  templateKindPhoto: 'Slika',
  templateNeedsPhoto: 'Izaberi sliku za ovaj šablon',
  templateApplied: 'Šablon je primenjen',
  templateColors: 'Boje',
  templateOccasion: 'Prilika',
  templateCount: '{n} šablona',
  templatePalette: 'Izaberi paletu boja',
  mustReplaceBadge: 'Obavezno',
  permanentRefused: 'Ovaj element je deo dizajna i ne može se obrisati.',
  notInteractive: 'Ovaj element je zaključan u dizajnu.',

  // ── Background ────────────────────────────────────────────────────────────
  backgroundNote: 'Ova pozadina se štampa. Za providan dizajn izaberite „Bez pozadine”.',
  backgroundNone: 'Bez pozadine',
  backgroundPhoto: 'Dodaj fotografiju',
  backgroundPhotoNote: 'Fotografija se dodaje kao sloj, popunjava polje i ide na dno.',
};

export type DesignerExtraTexts = typeof DESIGNER_EXTRA_TEXTS;
export type DesignerAllTexts = DesignerTexts & DesignerExtraTexts;

/**
 * What each validation code means to a shopper. The codes are shared with the
 * API (which refuses on the same list), so a shopper reads the same reason the
 * commit would give — never "error 422".
 */
const CORE_PROBLEM_TEXTS = {
  'bad-answer': 'Nešto nije u redu sa ovim elementom.',
  'too-many-layers': 'Previše elemenata u polju za štampu.',
  'kind-not-allowed': 'Ovaj tip elementa nije dozvoljen na ovom proizvodu.',
  'bad-geometry': 'Element je nemoguće veličine.',
  'outside-zone': 'Deo dizajna je van polja za štampu.',
  'text-empty': 'Upiši tekst ili obriši ovaj element.',
  'text-too-long': 'Tekst je predugačak.',
  'too-many-lines': 'Previše redova u tekstu.',
  'font-not-allowed': 'Ovaj font nije dostupan.',
  'color-not-allowed': 'Ova boja nije dostupna.',
  'size-out-of-range': 'Slova su premala ili prevelika za štampu.',
  'too-many-images': 'Previše slika u polju za štampu.',
  'low-resolution': 'Slika može biti malo mekša na štampi — može se koristiti.',
  'library-not-allowed': 'Ovaj ukras nije dostupan na ovom proizvodu.',
  'shape-not-allowed': 'Ovaj oblik nije dostupan.',
  'asset-unknown': 'Slika više nije dostupna — dodaj je ponovo.',
  'template-unreplaced': 'Zamenite šablonski sadržaj svojim tekstom ili slikom.',
  'sticker-edge': 'Beli okvir dodiruje ivicu polja za štampu — smanjite ili pomerite dizajn.',
  'sticker-islands': 'Dizajn se raspada na više odvojenih nalepnica — spojite delove ili povećajte spojeve.',
} satisfies Record<DesignProblemCode, string>;

/**
 * Codes the platform is growing into. They are kept OUTSIDE the typed record
 * on purpose: the designer must be able to explain a sticker problem the
 * moment the scene package starts reporting it, without this file failing to
 * compile against a `DesignProblemCode` union that does not carry it yet (and
 * without going silent the day it does).
 */
const EXTRA_PROBLEM_TEXTS: Record<string, string> = {
  'sticker-empty': 'Dodajte nešto u dizajn da bi nalepnica imala oblik.',
};

export const PROBLEM_TEXTS: Record<string, string> = { ...CORE_PROBLEM_TEXTS, ...EXTRA_PROBLEM_TEXTS };

/**
 * Problems a shopper must fix before the design can be committed. Everything
 * else is advice: a low-resolution warning, a layer over the safe margin.
 *
 * Every code the validator reports today blocks, so the set is "everything we
 * have a sentence for" rather than a second list to keep in step.
 */
const BLOCKING: ReadonlySet<string> = new Set(Object.keys(PROBLEM_TEXTS));

export function isBlocking(problem: DesignProblem): boolean {
  return BLOCKING.has(problem.code);
}

export function problemMessage(problem: DesignProblem): string {
  return PROBLEM_TEXTS[problem.code] ?? PROBLEM_TEXTS['bad-answer']!;
}

/** `{name}` → the value; unknown placeholders are left alone, never blanked. */
export function fillText(template: string, vars: Record<string, string | number> = {}): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => {
    const value = vars[key];
    return value === undefined ? whole : String(value);
  });
}

/**
 * The full text table with the shop's overrides on top. Unknown keys in the
 * override object are ignored rather than shown, so a stale `display.labels`
 * entry can never blank a button.
 */
export function resolveTexts(overrides?: Partial<DesignerAllTexts>): DesignerAllTexts {
  const base: DesignerAllTexts = { ...DEFAULT_DESIGNER_TEXTS, ...DESIGNER_EXTRA_TEXTS };
  if (!overrides) return base;
  const out = { ...base } as Record<string, string>;
  for (const [key, value] of Object.entries(overrides as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim() && key in base) out[key] = value;
  }
  return out as unknown as DesignerAllTexts;
}
